
import React, { useState } from "react";
import { 
  Table, 
  TableBody, 
  TableCaption, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { Download, Edit, Trash2, Plus } from "lucide-react";

// Sample data for roles
const sampleRoles = [
  {
    id: "1",
    title: "Store Manager",
    accessLevel: "Admin",
    modules: ["Sales", "POS", "Inventory", "Loyalty", "Reports", "Employee Management"],
    outletAccess: ["All Outlets"]
  },
  {
    id: "2",
    title: "Supervisor",
    accessLevel: "Write",
    modules: ["Sales", "POS", "Inventory", "Loyalty"],
    outletAccess: ["Main Branch", "North Branch"]
  },
  {
    id: "3",
    title: "Cashier",
    accessLevel: "Read",
    modules: ["POS", "Inventory"],
    outletAccess: ["Main Branch"]
  }
];

// Available modules
const availableModules = [
  "Sales", 
  "POS", 
  "Inventory", 
  "Loyalty", 
  "Reports", 
  "Employee Management", 
  "Customer Management"
];

// Available outlets
const availableOutlets = [
  "Main Branch", 
  "North Branch", 
  "East Branch", 
  "South Branch", 
  "West Branch"
];

interface RoleFormData {
  title: string;
  accessLevel: string;
  selectedModules: string[];
  selectedOutlets: string[];
}

const RolesPermissions = () => {
  const [roles, setRoles] = useState(sampleRoles);
  const [isAddRoleOpen, setIsAddRoleOpen] = useState(false);
  const [moduleSelections, setModuleSelections] = useState<Record<string, string[]>>(
    sampleRoles.reduce((acc, role) => ({
      ...acc,
      [role.id]: role.modules
    }), {})
  );
  
  const form = useForm<RoleFormData>({
    defaultValues: {
      title: "",
      accessLevel: "Read",
      selectedModules: [],
      selectedOutlets: []
    }
  });

  const handleAddRole = (data: RoleFormData) => {
    const newRole = {
      id: `${roles.length + 1}`,
      title: data.title,
      accessLevel: data.accessLevel,
      modules: data.selectedModules,
      outletAccess: data.selectedOutlets
    };
    
    setRoles([...roles, newRole]);
    setModuleSelections({
      ...moduleSelections,
      [newRole.id]: data.selectedModules
    });
    setIsAddRoleOpen(false);
    form.reset();
  };

  const handleDeleteRole = (id: string) => {
    setRoles(roles.filter(role => role.id !== id));
    const updatedSelections = { ...moduleSelections };
    delete updatedSelections[id];
    setModuleSelections(updatedSelections);
  };

  const handleToggleModule = (roleId: string, module: string) => {
    const currentModules = moduleSelections[roleId] || [];
    let updatedModules;
    
    if (currentModules.includes(module)) {
      updatedModules = currentModules.filter(m => m !== module);
    } else {
      updatedModules = [...currentModules, module];
    }
    
    setModuleSelections({
      ...moduleSelections,
      [roleId]: updatedModules
    });
    
    setRoles(roles.map(role => 
      role.id === roleId 
        ? { ...role, modules: updatedModules } 
        : role
    ));
  };

  const handleExportRoles = () => {
    // In a real app, this would generate and download an Excel file
    alert("Exporting roles structure... This feature would be implemented with a proper Excel export library");
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold">Roles & Permissions</h2>
        <div className="flex space-x-2">
          <Dialog open={isAddRoleOpen} onOpenChange={setIsAddRoleOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Role
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>Create New Role</DialogTitle>
              </DialogHeader>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleAddRole)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Role Title</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter role title" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="accessLevel"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Access Level</FormLabel>
                        <FormControl>
                          <Select 
                            onValueChange={field.onChange} 
                            defaultValue={field.value}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select access level" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Read">Read</SelectItem>
                              <SelectItem value="Write">Write</SelectItem>
                              <SelectItem value="Admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="selectedModules"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Module Access</FormLabel>
                        <div className="grid grid-cols-2 gap-2 border p-4 rounded-md">
                          {availableModules.map(module => (
                            <div key={module} className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                id={`module-${module}`}
                                value={module}
                                checked={field.value.includes(module)}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const updatedValue = e.target.checked
                                    ? [...field.value, value]
                                    : field.value.filter(v => v !== value);
                                  field.onChange(updatedValue);
                                }}
                              />
                              <label htmlFor={`module-${module}`} className="text-sm">
                                {module}
                              </label>
                            </div>
                          ))}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="selectedOutlets"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Outlet Access</FormLabel>
                        <div className="grid grid-cols-2 gap-2 border p-4 rounded-md">
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="outlet-all"
                              value="All Outlets"
                              checked={field.value.includes("All Outlets")}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  field.onChange(["All Outlets"]);
                                } else {
                                  field.onChange([]);
                                }
                              }}
                            />
                            <label htmlFor="outlet-all" className="text-sm font-medium">
                              All Outlets
                            </label>
                          </div>
                          
                          {availableOutlets.map(outlet => (
                            <div key={outlet} className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                id={`outlet-${outlet}`}
                                value={outlet}
                                checked={field.value.includes(outlet) || field.value.includes("All Outlets")}
                                disabled={field.value.includes("All Outlets")}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const updatedValue = e.target.checked
                                    ? [...field.value.filter(v => v !== "All Outlets"), value]
                                    : field.value.filter(v => v !== value);
                                  field.onChange(updatedValue);
                                }}
                              />
                              <label 
                                htmlFor={`outlet-${outlet}`} 
                                className={`text-sm ${field.value.includes("All Outlets") ? "text-muted-foreground" : ""}`}
                              >
                                {outlet}
                              </label>
                            </div>
                          ))}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="flex justify-end">
                    <Button type="submit">Create Role</Button>
                  </div>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
          
          <Button variant="outline" onClick={handleExportRoles}>
            <Download className="mr-2 h-4 w-4" />
            Export Structure
          </Button>
        </div>
      </div>
      
      <div className="rounded-md border">
        <Table>
          <TableCaption>Current roles and their permissions</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px]">Role Title</TableHead>
              <TableHead>Access Level</TableHead>
              <TableHead>Module Access</TableHead>
              <TableHead>Outlet Access</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {roles.map((role) => (
              <TableRow key={role.id}>
                <TableCell className="font-medium">{role.title}</TableCell>
                <TableCell>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    role.accessLevel === "Admin" 
                      ? "bg-purple-100 text-purple-800" 
                      : role.accessLevel === "Write"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-gray-100 text-gray-800"
                  }`}>
                    {role.accessLevel}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {availableModules.map(module => (
                      <div key={module} className="flex items-center mr-4 mb-2">
                        <Switch
                          checked={moduleSelections[role.id]?.includes(module)}
                          onCheckedChange={() => handleToggleModule(role.id, module)}
                          id={`${role.id}-${module}`}
                        />
                        <label htmlFor={`${role.id}-${module}`} className="ml-2 text-xs">
                          {module}
                        </label>
                      </div>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {role.outletAccess.map(outlet => (
                      <span key={outlet} className="inline-block px-2 py-1 bg-gray-100 rounded-full text-xs">
                        {outlet}
                      </span>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" size="icon">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon" 
                      onClick={() => handleDeleteRole(role.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default RolesPermissions;
